import { Box, Typography, Tooltip, alpha, useTheme } from '@mui/material';

const UserActivityChart = ({ timeline }) => {
  const theme = useTheme();

  if (!timeline || timeline.length === 0) {
    return (
      <Box sx={{
        p: 3, mb: 3, borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Activity (last 90 days)</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>No activity data available.</Typography>
      </Box>
    );
  }

  const maxEvents = Math.max(...timeline.map((d) => d.events), 1);

  const getColor = (count) => {
    if (count === 0) return theme.palette.mode === 'dark' ? alpha('#fff', 0.05) : alpha('#000', 0.04);
    const intensity = Math.min(count / maxEvents, 1);
    if (intensity < 0.25) return alpha(theme.palette.primary.main, 0.2);
    if (intensity < 0.5) return alpha(theme.palette.primary.main, 0.4);
    if (intensity < 0.75) return alpha(theme.palette.primary.main, 0.65);
    return theme.palette.primary.main;
  };

  // Fill in missing days from 90 days ago to today
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 89);
  const dayMap = {};
  timeline.forEach((d) => { dayMap[d.date] = d; });

  const days = [];
  for (let d = new Date(start); d <= today; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split('T')[0];
    days.push(dayMap[key] || { date: key, events: 0, sessions: 0, searches: 0 });
  }

  // Split into weeks (columns of 7)
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <Box sx={{
      p: 3, mb: 3, borderRadius: '12px',
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: theme.palette.background.paper,
    }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
        Activity (last 90 days)
      </Typography>
      <Box sx={{ display: 'flex', gap: '3px', overflowX: 'auto', pb: 1 }}>
        {weeks.map((week, wi) => (
          <Box key={wi} sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {week.map((day) => (
              <Tooltip
                key={day.date}
                title={`${day.date}: ${day.events} events, ${day.sessions} sessions, ${day.searches} searches`}
                arrow
                placement="top"
              >
                <Box sx={{
                  width: 14, height: 14, borderRadius: '3px',
                  bgcolor: getColor(day.events),
                  cursor: 'default',
                  transition: 'transform 0.1s',
                  '&:hover': { transform: 'scale(1.3)' },
                }} />
              </Tooltip>
            ))}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 0.5 }}>Less</Typography>
        {[0, 0.2, 0.4, 0.65, 1].map((intensity, i) => (
          <Box key={i} sx={{
            width: 12, height: 12, borderRadius: '2px',
            bgcolor: intensity === 0
              ? (theme.palette.mode === 'dark' ? alpha('#fff', 0.05) : alpha('#000', 0.04))
              : alpha(theme.palette.primary.main, intensity),
          }} />
        ))}
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 0.5 }}>More</Typography>
      </Box>
    </Box>
  );
};

export default UserActivityChart;
